import React from 'react';

import {
  AttentionCircleFilledIcon, PlusIcon, SettingsIcon, SlidersIcon,
} from '@vlabs/icons';
import { AnchorLayout } from '@vlabs/shared/components/anchor-layout';
import {
  SideMenu, Badge, Panel, Control,
} from '@vlabs/uikit';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { AnalyticsDropdown } from '@vlabs/pages/video-analytics/streams/components/AnalyticsDropdown';

import { AnalyticSectionHeader } from '../AnalyticSectionHeader';
import Geoposition from '../Geoposition';
import { RegionsEdit } from '../RegionsEdit';
import { SubForms } from './sub-forms';
import { AnalyticsArrayProvider, useAnalyticsContext } from './useAnalyticsContext';
import st from './VideoAnalyticsForm.module.sass';

function VideoAnalyticsForm() {
  const { t } = useTranslation();
  const { hash } = useLocation();
  const {
    activeSection,
    onChangeActiveSection,
    addAnalytic,
    analyticFields,
  } = useAnalyticsContext();
  const { formState: { errors } } = useFormContext();

  return (
    <div className={st.Root}>
      <div className={st.Menu}>
        <div className={st.MenuSection}>
          <SideMenu.MenuItem
            autoFocus
            isActive={activeSection === 'common'}
            isOpened
            leftIcon={<SettingsIcon />}
            rightIcon={['name', 'description', 'group_name', 'data', 'autorestart']
              .some((field) => errors[field]) && <AttentionCircleFilledIcon className={st.ErrorIcon} />}
            value={t('videoAnalytics:editAndCreateStreamsPageTitles.main_parameters')}
          >
            <SideMenu.MenuItem
              addon={['name', 'description', 'group_name'].some((field) => errors[field]) && (
              <Badge
                kind="negative"
                shape="dot"
              />
              )}
              href="#common"
              isActive={hash === '#common'}
              onClick={() => onChangeActiveSection('common')}
              value={t('videoAnalytics:editAndCreateStreamsPageTitles.basic_parameters')}
            />
            <SideMenu.MenuItem
              addon={!!errors.data && (
              <Badge
                kind="negative"
                shape="dot"
              />
              )}
              href="#stream-data"
              isActive={hash === '#stream-data'}
              onClick={() => onChangeActiveSection('common')}
              value={t('videoAnalytics:editAndCreateStreamsPageTitles.stream_data')}
            />
            <SideMenu.MenuItem
              addon={!!errors.autorestart && (
              <Badge
                kind="negative"
                shape="dot"
              />
              )}
              href="#restart-options"
              isActive={hash === '#restart-options'}
              onClick={() => onChangeActiveSection('common')}
              value={t('videoAnalytics:editAndCreateStreamsPageTitles.restart_options')}
            />
          </SideMenu.MenuItem>
          <SideMenu.MenuItemDivider />
        </div>
        <div className={st.MenuSection}>
          <SideMenu.MenuItem
            isActive={activeSection === 'analytics'}
            isOpened
            leftIcon={<SlidersIcon />}
            rightIcon={!!errors.analytics && <AttentionCircleFilledIcon className={st.ErrorIcon} />}
            value={t('videoAnalytics:table_header.videoanalytics')}
          >
            {analyticFields.map((analytic) => (
              <SideMenu.MenuItem
                addon={!!analytic.hasError && (
                <Badge
                  kind="negative"
                  shape="dot"
                />
                )}
                href={`#${analytic.id}`}
                isActive={hash === `#${analytic.id}`}
                key={analytic.key}
                onClick={() => onChangeActiveSection('analytics')}
                value={analytic.labelWithOrdinal}
              />
            ))}
            <div className={st.AnalyticsDropdown__Btn}>
              <AnalyticsDropdown
                onAnalyticClick={(type) => addAnalytic(type)}
                renderTrigger={(props) => (
                  <Control.Button
                    icon={<PlusIcon />}
                    size="small"
                    type="button"
                    variant="flat"
                    {...props}
                  >
                    {t('btn.add')}
                  </Control.Button>
                )}
              />
            </div>
          </SideMenu.MenuItem>
          <SideMenu.MenuItemDivider />
        </div>
      </div>

      <div className={st.Main}>
        {activeSection === 'common' && (
          <AnchorLayout.Group>
            <AnchorLayout.Section
              rootProps={{ id: 'common' }}
              title={t('videoAnalytics:editAndCreateStreamsPageTitles.basic_parameters')}
            >
              <SubForms.BasicParametersSubForm />
            </AnchorLayout.Section>
            <div className={st.Divider} />

            <AnchorLayout.Section
              rootProps={{ id: 'stream-data' }}
              title={t('videoAnalytics:editAndCreateStreamsPageTitles.stream_data')}
            >
              <SubForms.StreamDataSubForm />
            </AnchorLayout.Section>
            <div className={st.Divider} />

            <AnchorLayout.Section
              rootProps={{ id: 'restart-options' }}
              title={t('videoAnalytics:editAndCreateStreamsPageTitles.restart_options')}
            >
              <SubForms.RestartOptionsSubForm />
            </AnchorLayout.Section>
          </AnchorLayout.Group>
        )}
        {activeSection === 'analytics' && (
          <AnchorLayout.Group>
            {analyticFields.map((analytic, index) => {
              const isLastElement = index === analyticFields.length - 1;
              return (
                <div key={analytic.key}>
                  <AnchorLayout.Section rootProps={{ id: analytic.id }}>
                    <AnalyticSectionHeader
                      label={analytic.labelWithOrdinal}
                      onDelete={analytic.remove}
                      status={analytic?.status}
                    />
                    <analytic.Form fieldName={`analytics.${index}`} />
                  </AnchorLayout.Section>
                  {!isLastElement && <div className={st.Divider} />}
                </div>
              );
            })}
          </AnchorLayout.Group>
        )}
      </div>

      <Panel className={st.RightSection}>
        <RegionsEdit />
        <Geoposition />
      </Panel>

    </div>
  );
}

function VideoAnalyticsFormWithContext() {
  return (
    <AnalyticsArrayProvider>
      <VideoAnalyticsForm />
    </AnalyticsArrayProvider>
  );
}

export { VideoAnalyticsFormWithContext as VideoAnalyticsForm };
